import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  children,
  width = 480,
  dismissible = true,
  hideHeader = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: number;
  dismissible?: boolean;
  /**
   * Drop the title bar and let the content own the top row. `title` still names
   * the dialog for assistive tech, so this only ever removes chrome.
   */
  hideHeader?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Escape to close + Tab/Shift+Tab focus trap within the dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const card = cardRef.current;
      if (!card) return;
      const focusable = Array.from(card.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) {
        e.preventDefault();
        card.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismissible, open, onClose]);

  // Lock body scroll while the modal is open.
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Move focus into the dialog on open, and restore it on close.
  useEffect(() => {
    if (!open) return;
    const card = cardRef.current;
    if (!card) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    if (!card.contains(document.activeElement)) {
      const focusable = card.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (focusable ?? card).focus();
    }
    return () => {
      if (previouslyFocused && document.body.contains(previouslyFocused)) previouslyFocused.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && dismissible && onClose()}
    >
      <div
        ref={cardRef}
        className={`modal-card${hideHeader ? " modal-card-bare" : ""}`}
        style={{ width }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hideHeader ? undefined : titleId}
        aria-label={hideHeader ? title : undefined}
        tabIndex={-1}
      >
        {hideHeader ? null : (
          <div className="modal-header">
            <h2 id={titleId}>{title}</h2>
            {dismissible ? (
              <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label={`Close ${title}`} title="Close">
                ✕
              </button>
            ) : null}
          </div>
        )}
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
